/**
 * @fileoverview Audit client factory for local-first audit log operations.
 * @module audit/client
 *
 * Responsibilities:
 * - Factory function to create app-specific audit clients
 * - Write audit entries to local Dexie audit_logs table
 * - Read/filter audit entries from local Dexie
 * - Sync remote audit entries into local store
 *
 * Dependencies:
 * - ./collections (resolveAuditCollection, matchesAuditCollection)
 * - ./events (dispatchAuditLogWritten)
 * - ./types (AuditLogEntry, WriteAuditLogOptions, CreateAuditClientConfig)
 *
 * Architecture:
 * - Local-first. All writes go to Dexie first.
 * - onLocalWrite callback triggers sync push.
 * - Remote sync is pull-based (fetchRemoteLogs → merge into Dexie).
 *
 * Notes:
 * - Each app (tenant-web, system-web, mobile-pwa) creates its own client instance
 * - requireUserForWrites enforces actor presence; set false for system operations
 * - fetchAuditLogs awaits remote merge so tenant-web Audit tabs see mobile rows on first paint
 */

import { getAuditCollectionCandidates, matchesAuditCollection, resolveAuditCollection } from './collections';
import { dispatchAuditLogWritten } from './events';
import type {
  AuditApiListResponse,
  AuditLogEntry,
  AuditLogFetchOptions,
  AuditLogFetchResult,
  AuditLogListParams,
  AuditLogListResult,
  AuditUser,
  CreateAuditClientConfig,
  WriteAuditLogOptions,
} from './types';

function generateAuditId(): string {
  return `audit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function stringifyId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value && 'toHexString' in value && typeof (value as { toHexString: () => string }).toHexString === 'function') {
    return (value as { toHexString: () => string }).toHexString();
  }
  return String(value);
}

function toIsoString(value: unknown): string {
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  return new Date().toISOString();
}

/**
 * @process normalizeAuditEntry
 *
 * Steps:
 * 1. Extract and coerce fields from a raw audit record.
 * 2. Apply defaults for missing fields (action, performedBy, source).
 * 3. Return a well-formed AuditLogEntry.
 *
 * Inputs:
 * - raw: unknown — raw audit record from API or Dexie.
 * - sourceApp: string — fallback source app identifier.
 *
 * Outputs:
 * - AuditLogEntry — normalized, well-formed entry.
 *
 * Failure Handling:
 * - Gracefully handles null/undefined fields via defaults.
 *
 * Idempotency: NOT REQUIRED — _id fallback uses Date.now() + Math.random().
 */
function normalizeAuditEntry(raw: any, sourceApp: string): AuditLogEntry {
  return {
    _id: stringifyId(raw?._id) || generateAuditId(),
    collection: resolveAuditCollection(String(raw?.collection ?? '')),
    documentId: stringifyId(raw?.documentId),
    action: raw?.action ?? 'update',
    performedBy: raw?.performedBy ?? {
      userId: raw?.userId ?? 'unknown',
      email: raw?.userEmail ?? '',
      name: raw?.userName ?? 'Unknown User',
    },
    summary: raw?.summary ?? `${raw?.action ?? 'update'} by ${raw?.userName ?? 'Unknown User'}`,
    changes: Array.isArray(raw?.changes) ? raw.changes : [],
    before: raw?.before,
    after: raw?.after,
    source: {
      app: raw?.source?.app ?? sourceApp,
      deviceType: raw?.source?.deviceType ?? 'desktop',
    },
    metadata: raw?.metadata,
    createdAt: toIsoString(raw?.createdAt),
    updatedAt: toIsoString(raw?.updatedAt ?? raw?.createdAt),
  };
}

function getResponseItems(response: AuditApiListResponse): unknown[] {
  if (Array.isArray(response.items)) return response.items;
  if (Array.isArray(response.data)) return response.data;
  return [];
}

/**
 * @process createAuditClient
 *
 * Steps:
 * 1. Accept app-specific configuration (Dexie accessor, source app, user resolver).
 * 2. Return an object with writeAuditLog, fetchAuditLogs, listAuditLogs methods.
 *
 * Inputs:
 * - config: CreateAuditClientConfig — app-specific audit configuration.
 *
 * Outputs:
 * - Object with writeAuditLog, fetchAuditLogs, listAuditLogs, normalizeAuditEntry.
 *
 * Failure Handling:
 * - writeAuditLog throws if requireUserForWrites is true and no user is available.
 * - syncRemoteEntries silently catches network errors; fetch/list still return local Dexie rows.
 *
 * Idempotency: NOT REQUIRED — writeAuditLog creates a new entry each call.
 *
 * @localFirst
 * Source of Truth: local (Dexie audit_logs table)
 * Write Flow: write to Dexie → dispatch event → onLocalWrite triggers sync push
 * Sync Strategy: pull-based remote sync merges server entries into Dexie
 * Conflict Resolution: last-write-wins via Dexie put (upsert by _id)
 */
export function createAuditClient(config: CreateAuditClientConfig) {
  const resolveUser = (user?: AuditUser): AuditUser | null => user ?? config.getCurrentUser?.() ?? null;
  const assertCanWriteAuditLog = (opts: Pick<WriteAuditLogOptions, 'collection' | 'action' | 'performedBy' | 'metadata'>): void => {
    const resolvedUser = resolveUser(opts.performedBy);
    const allowSystemActor = opts.metadata?.systemGenerated === true && !!opts.performedBy?.userId;

    if (config.requireUserForWrites && !resolvedUser && !allowSystemActor) {
      throw new Error(`[audit] authenticated actor is required for ${opts.collection}:${opts.action}`);
    }
  };

  const syncRemoteEntries = async (params: URLSearchParams): Promise<void> => {
    if (!config.fetchRemoteLogs) return;

    try {
      const response = await config.fetchRemoteLogs(params);
      const entries = getResponseItems(response).map((entry) => normalizeAuditEntry(entry, config.sourceApp));
      if (entries.length === 0) return;
      const db = await config.getDb();
      const changed: AuditLogEntry[] = [];
      for (const entry of entries) {
        const existing = await db.audit_logs.get(entry._id);
        if (existing && JSON.stringify(normalizeAuditEntry(existing, config.sourceApp)) === JSON.stringify(entry)) continue;
        await db.audit_logs.put(entry as unknown as Record<string, unknown>);
        changed.push(entry);
      }
      // Notify listeners only when Dexie actually changed. Re-dispatching for
      // unchanged rows made every panel refetch -> dispatch -> refetch forever.
      const seen = new Set<string>();
      for (const entry of changed) {
        const key = `${entry.collection}::${entry.documentId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        dispatchAuditLogWritten({ collection: entry.collection, documentId: entry.documentId });
      }
    } catch {
      return;
    }
  };

  const readLocalEntries = async (): Promise<AuditLogEntry[]> => {
    const db = await config.getDb();
    const docs = await db.audit_logs.toArray();
    return docs.map((doc) => normalizeAuditEntry(doc, config.sourceApp));
  };

  const writeAuditLog = async (opts: WriteAuditLogOptions): Promise<void> => {
    assertCanWriteAuditLog(opts);
    const resolvedUser = resolveUser(opts.performedBy);

    const now = new Date().toISOString();
    const entry: AuditLogEntry = {
      _id: generateAuditId(),
      collection: resolveAuditCollection(opts.collection),
      documentId: stringifyId(opts.documentId),
      action: opts.action,
      performedBy: resolvedUser ?? opts.performedBy ?? config.defaultPerformedBy,
      summary: opts.summary,
      changes: opts.changes ?? [],
      before: opts.before,
      after: opts.after,
      source: {
        app: opts.source?.app ?? config.sourceApp,
        deviceType: opts.source?.deviceType ?? 'desktop',
      },
      metadata: opts.metadata,
      createdAt: now,
      updatedAt: now,
    };

    const db = await config.getDb();
    await db.audit_logs.put(entry as unknown as Record<string, unknown>);
    dispatchAuditLogWritten({ collection: entry.collection, documentId: entry.documentId });
    await config.onLocalWrite?.('audit_logs');
  };

  const fetchAuditLogs = async (
    collection: string,
    documentId: string,
    options: AuditLogFetchOptions = {},
  ): Promise<AuditLogFetchResult> => {
    const { limit = 20, skip = 0, action } = options;
    const canonicalCollection = resolveAuditCollection(collection);
    const params = new URLSearchParams({
      collection: canonicalCollection,
      documentId: stringifyId(documentId),
      limit: '100',
      page: '1',
    });
    if (action) params.set('action', action);

    await syncRemoteEntries(params);

    let entries = (await readLocalEntries()).filter((entry) => (
      matchesAuditCollection(entry.collection, canonicalCollection)
      && stringifyId(entry.documentId) === stringifyId(documentId)
    ));

    if (action) entries = entries.filter((entry) => entry.action === action);
    entries.sort((left, right) => right.createdAt.localeCompare(left.createdAt));

    return {
      data: entries.slice(skip, skip + limit),
      total: entries.length,
      limit,
      skip,
    };
  };

  const listAuditLogs = async (params: AuditLogListParams = {}): Promise<AuditLogListResult> => {
    const { collection, action, userId, limit = 20, page = 1 } = params;
    const skip = (page - 1) * limit;
    const remoteParams = new URLSearchParams({ limit: '100', page: '1' });
    const canonicalCollection = collection ? resolveAuditCollection(collection) : undefined;

    if (canonicalCollection) remoteParams.set('collection', canonicalCollection);
    if (action) remoteParams.set('action', action);
    if (userId) remoteParams.set('userId', userId);

    await syncRemoteEntries(remoteParams);

    let entries = await readLocalEntries();

    if (canonicalCollection) {
      const allowed = new Set(getAuditCollectionCandidates(canonicalCollection));
      entries = entries.filter((entry) => allowed.has(resolveAuditCollection(entry.collection)));
    }
    if (action) entries = entries.filter((entry) => entry.action === action);
    if (userId) {
      entries = entries.filter((entry) => (
        entry.performedBy?.userId === userId
        || entry.performedBy?.email?.includes(userId)
      ));
    }

    entries.sort((left, right) => right.createdAt.localeCompare(left.createdAt));

    return {
      data: entries.slice(skip, skip + limit),
      total: entries.length,
    };
  };

  return {
    assertCanWriteAuditLog,
    writeAuditLog,
    fetchAuditLogs,
    listAuditLogs,
    normalizeAuditEntry,
  };
}
